import { useEffect, useMemo, useRef } from "react";
import { Mic2 } from "lucide-react";

// ── helpers ──────────────────────────────────────────────────────────────────

function flattenLines(structure) {
  const flat = [];
  for (const sec of (structure?.sections || [])) {
    for (const line of (sec.lines || [])) {
      if (!(line.text || "").trim()) continue; // skip chord-only / empty lines
      flat.push({
        sectionName: sec.name || "",
        globalIndex: flat.length,
        start: line.start || 0,
        end: line.end || 0,
        chord: line.chord || null,
        text: line.text || "",
      });
    }
  }
  return flat;
}

function getActiveIndex(flat, currentTime) {
  // Last line whose start <= currentTime
  let active = -1;
  for (let i = 0; i < flat.length; i++) {
    if (currentTime >= flat[i].start) active = i;
    else break;
  }
  return active;
}

// ── main component ────────────────────────────────────────────────────────────

/**
 * LyricsKaraokeView — Spotify-style synced lyrics.
 *
 * Props:
 *   structure   {object|null}  from GET /api/global-songs/{id}/structure
 *   currentTime {number}       seconds from the YouTube player
 *   onLineClick {function}     (start_time) → seek player to that position
 */
export default function LyricsKaraokeView({ structure, currentTime = 0, onLineClick }) {
  const flat = useMemo(() => flattenLines(structure), [structure]);
  const activeIndex = useMemo(() => getActiveIndex(flat, currentTime), [flat, currentTime]);

  const lineRefs = useRef([]);
  const lastScrolled = useRef(-1);
  const containerRef = useRef(null);

  // Auto-scroll: center the active line each time it changes
  useEffect(() => {
    if (activeIndex < 0 || activeIndex === lastScrolled.current) return;
    lastScrolled.current = activeIndex;
    const el = lineRefs.current[activeIndex];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIndex]);

  // ── empty state ─────────────────────────────────────────────────────────────
  if (!flat.length) {
    return (
      <div className="grid min-h-64 place-items-center p-6 text-center">
        <div>
          <Mic2 className="mx-auto mb-3 h-8 w-8 text-slate-600" />
          <p className="text-sm font-medium text-slate-400">
            Letra no disponible
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Usa &quot;Re-transcribir con Whisper&quot; en el panel de edición para generar la letra sincronizada.
          </p>
        </div>
      </div>
    );
  }

  // ── karaoke lines ────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="overflow-y-auto max-h-[58vh] px-4 py-10 space-y-0.5 scroll-smooth"
    >
      {flat.map((line, gi) => {
        const delta = gi - activeIndex;
        const isActive = delta === 0 && activeIndex >= 0;
        const isPast = gi < activeIndex;

        // Text size + opacity classes per position
        let sizeClass;
        let opacityClass;
        if (isActive) {
          sizeClass = "text-2xl font-bold";
          opacityClass = "opacity-100 text-white";
        } else if (delta === 1) {
          sizeClass = "text-base font-medium";
          opacityClass = "opacity-75 text-slate-200";
        } else if (delta === 2) {
          sizeClass = "text-sm";
          opacityClass = "opacity-55 text-slate-300";
        } else if (isPast) {
          sizeClass = "text-sm";
          opacityClass = "opacity-35 text-slate-500";
        } else {
          sizeClass = "text-xs";
          opacityClass = "opacity-30 text-slate-500";
        }

        return (
          <div
            key={`${line.sectionName}-${gi}`}
            ref={(el) => { lineRefs.current[gi] = el; }}
            className={`transition-all duration-300 py-2 px-3 rounded-xl cursor-pointer select-none ${
              isActive ? "bg-white/[0.05]" : "hover:bg-white/[0.025]"
            }`}
            onClick={() => onLineClick?.(line.start)}
          >
            {/* Chord — shown only above the active line */}
            {isActive && line.chord && (
              <p className="mb-0.5 text-sm font-bold tracking-widest text-cyan-300 transition-all duration-300">
                {line.chord}
              </p>
            )}
            {/* Lyric text */}
            <p className={`${sizeClass} ${opacityClass} transition-all duration-300 leading-snug`}>
              {line.text}
            </p>
          </div>
        );
      })}
      {/* Bottom padding so last line can scroll to center */}
      <div className="h-40" />
    </div>
  );
}
